import { ChevronRight, Flag, Trophy, Undo2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { ecartAuPar } from '../../lib/rounds.ts';

interface Props {
    trou: number;
    nbTrous: number;
    coups: number;
    par: number | null;
    /** Les clubs joués, dans l'ordre. La trace du trou en trois mots. */
    clubs: string[];
    /** L'icône du score, choisie par l'appelant : c'est lui qui connaît déjà
     *  le vocabulaire d'oiseaux de la carte de score. */
    icone: ReactNode;
    /** Où l'on se situe, quand il y a quelqu'un d'autre sur le parcours. */
    voisin: { nom: string; ecart: number } | null;
    /** L'écart au par sur l'ensemble du tour, après ce trou. */
    ecartTour: number | null;
    /** La balle a été relevée : le score est un maximum retenu, pas un score
     *  joué jusqu'au fond. L'écran le dit, et ne fête rien. */
    releve?: boolean;
    onSuivant: () => void;
    /** La balle n'était pas rentrée : on rouvre le trou.
     *
     *  Un appui de travers sur « La balle est rentrée » ou sur « Rentré »
     *  ferme le trou et fait apparaître cet écran ; sans cette porte il
     *  faudrait passer au trou suivant puis revenir par la liste des trous
     *  pour défaire. Trois gestes pour un tap raté. */
    onAnnuler: () => void;
}

/** Les confettis, posés une fois pour toutes.
 *
 *  Un tirage pseudo-aléatoire SEMÉ par le numéro du trou : deux rendus du même
 *  trou donnent la même pluie, sinon les confettis sautent d'une position à
 *  l'autre à chaque changement d'état de la page. C'est aussi ce qui fait que
 *  le trou 3 et le trou 12 n'ont pas la même, alors qu'un tableau figé se
 *  reconnaîtrait au deuxième trou.
 *
 *  Un générateur à congruence linéaire tient en deux lignes et n'a pas de
 *  dépendance : on ne fait pas venir une bibliothèque pour vingt rectangles. */
function confettis(graine: number): {
    x: number; y: number; delai: number; duree: number; angle: number; ton: string;
}[] {
    let e = graine * 9301 + 49297;
    const suivant = (): number => {
        e = (e * 9301 + 49297) % 233280;
        return e / 233280;
    };
    const tons = ['var(--color-accent)', 'var(--zone-green)', 'var(--color-primary)', 'var(--tuile)'];
    return Array.from({ length: 22 }, () => ({
        x: Math.round(suivant() * 100),
        y: Math.round(suivant() * 100),
        delai: Math.round(suivant() * 2200),
        duree: 2600 + Math.round(suivant() * 2200),
        angle: Math.round(suivant() * 360),
        ton: tons[Math.floor(suivant() * tons.length)] ?? tons[0]!,
    }));
}

/* Le trou est fini : un écran, et rien d'autre.
 *
 * ── Pourquoi une page entière ───────────────────────────────────────────────
 *
 * Le score du trou tenait dans le bouton « Trou suivant », en bas de la carte
 * RÉSULTAT : un oiseau, un mot, une flèche. C'était juste, et complètement
 * muet — le seul moment de la partie où il se passe quelque chose passait dans
 * la même case que les six boutons qu'on tape en marchant.
 *
 * Un trou fini est le battement du parcours. C'est là qu'on lève les yeux,
 * qu'on annonce son score à voix haute, qu'on regarde où l'on en est par
 * rapport aux autres. L'écran s'arrête donc avec le joueur, le temps d'un tap.
 *
 * ── Ce qu'il montre, dans cet ordre ─────────────────────────────────────────
 *
 * Le nom du score en laiton — « Birdie » est ce qu'on dit, pas « −1 ». Puis
 * l'écart en très grand, parce que c'est ce qu'on retient. Puis la ligne des
 * faits : le trou, le nombre de coups, et les clubs dans l'ordre où on les a
 * sortis du sac — c'est le récit du trou en trois mots, et il vaut mieux que
 * n'importe quel graphique.
 *
 * Enfin, s'il y a quelqu'un d'autre, où l'on se situe par rapport à lui. Pas
 * « vous passez devant » : l'écran ne sait pas si l'ordre vient de changer, et
 * l'annoncer à tort serait pire que se taire. Il dit l'écart, qui est vrai.
 */
export function BilanTrou({
    trou, nbTrous, coups, par, clubs, icone, voisin, ecartTour, releve = false,
    onSuivant, onAnnuler,
}: Props) {
    const pluie = useMemo(() => confettis(trou), [trou]);
    /* Qui a demandé moins d'animations garde les confettis, POSÉS.
     *
     * Les supprimer entièrement rendait un écran nu là où les autres ont une
     * fête, et le réglage système demande moins de mouvement — pas moins de
     * joie. Ils sont donc éparpillés sur la page, comme s'ils étaient déjà
     * retombés, ce qui est d'ailleurs l'image du mockup.
     *
     * Lu une fois au montage : l'écran vit le temps d'un tap, et suivre un
     * changement de réglage pendant ce temps-là serait du travail pour
     * personne. */
    const anime = useMemo(
        () => window.matchMedia('(prefers-reduced-motion: no-preference)').matches,
        [],
    );
    const ecart = par != null ? coups - par : null;
    const dernier = trou >= nbTrous;
    /* Au-dessus du par, l'écran ne fête rien.
     *
     * Ni confettis, ni médaillon laiton : le laiton est la couleur de ce qui
     * est acquis, et un disque doré sous le mot « Double bogey » est une
     * félicitation pour un trou raté. L'écran reste — on a besoin de savoir où
     * l'on en est — mais il prend la brique, celle du coup qui coûte, et se
     * tait. */
    const fete = !releve && ecart != null && ecart <= 0;

    return (
        <div className="fixed inset-0 z-50 flex flex-col overflow-hidden bg-base-100 text-base-content">
            {/* La pluie est décorative et le dit : `aria-hidden`, et sans
                cible tactile. */}
            <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
                {fete && pluie.map((c, i) => (
                    <span key={i}
                        className="absolute block h-3 w-2 rounded-[1px]"
                        style={anime ? {
                            top: '-1.5rem',
                            left: `${c.x}%`,
                            background: c.ton,
                            animation: `confetti ${c.duree}ms linear ${c.delai}ms infinite`,
                            transform: `rotate(${c.angle}deg)`,
                        } : {
                            top: `${c.y}%`,
                            left: `${c.x}%`,
                            background: c.ton,
                            transform: `rotate(${c.angle}deg)`,
                            opacity: 0.75,
                        }} />
                ))}
            </div>

            <div className="safe-bottom relative mx-auto flex w-full max-w-md flex-1 flex-col items-center
                            justify-center gap-3 px-6 pt-[max(1.5rem,env(safe-area-inset-top))]">
                {/* Le médaillon laiton, comme sur le bilan du coup : deux fins
                    de geste qui se ressemblent, c'est un seul écran à
                    apprendre. */}
                <span className="flex size-24 items-center justify-center rounded-full shadow-lg [&_svg]:size-11"
                    style={fete
                        ? { background: 'var(--color-accent)', color: 'var(--color-accent-content)' }
                        : { background: 'var(--brique)', color: 'var(--brique-encre)' }}>
                    {icone}
                </span>

                {par != null && (
                    <>
                        <h1 className="font-display text-3xl font-bold leading-none tracking-tight"
                            style={{ color: fete ? 'var(--brass-ink)' : 'var(--brique-encre)' }}>
                            {/* Une balle relevée n'a pas de nom de score : le
                                joueur n'a pas fait double bogey, il s'est
                                arrêté. Écrire « Double » ici serait le premier
                                mensonge d'une carte. */}
                            {releve ? 'Balle relevée' : ecartAuPar(coups, par)}
                        </h1>
                        {/* L'écart en très grand : c'est le chiffre qu'on
                            retient, et le seul que l'on compare d'un trou à
                            l'autre. Le compte brut est sur la ligne d'en
                            dessous, avec le reste des faits. */}
                        <p className="font-score text-[6rem] leading-[0.85]"
                            style={{ color: fete ? 'var(--fairway-deep)' : 'var(--brique-encre)' }}>
                            {ecart === 0 ? 'par' : ecart! > 0 ? `+${ecart}` : `−${Math.abs(ecart!)}`}
                        </p>
                    </>
                )}

                {/* Le récit du trou : combien, et avec quoi. Les clubs dans
                    l'ordre où ils sont sortis du sac — une flèche entre chacun,
                    parce que c'est une suite et non une liste. */}
                <p className="text-center text-sm font-semibold opacity-70">
                    Trou {trou}
                    <span className="opacity-50">{' · '}</span>
                    <span className="font-bold opacity-100">{coups} coup{coups > 1 ? 's' : ''}</span>
                    {releve && <span className="opacity-50">{' · '}maximum retenu</span>}
                    {clubs.length > 0 && (
                        <>
                            <span className="opacity-50">{' · '}</span>
                            {clubs.join(' → ')}
                        </>
                    )}
                </p>

                {/* Où l'on se situe, quand il y a quelqu'un d'autre. La carte
                    blanche la détache du reste : c'est la seule information de
                    l'écran qui ne parle pas de soi. */}
                {voisin != null && (
                    <p className="flex items-center gap-2 rounded-full border border-base-300 bg-base-100
                                  px-4 py-2 text-sm font-semibold shadow-sm">
                        <Trophy size={16} aria-hidden="true" style={{ color: 'var(--brass-ink)' }} />
                        {voisin.ecart === 0
                            ? <>À égalité avec {voisin.nom}</>
                            : voisin.ecart < 0
                                ? <>Vous devancez {voisin.nom} de {Math.abs(voisin.ecart)}</>
                                : <>{voisin.nom} vous devance de {voisin.ecart}</>}
                        {ecartTour != null && (
                            <span className="opacity-60">
                                {' · '}
                                {ecartTour === 0 ? 'au par'
                                    : `${ecartTour > 0 ? '+' : '−'}${Math.abs(ecartTour)} au par`}
                            </span>
                        )}
                    </p>
                )}

                <div className="mt-2 flex w-full flex-col gap-1">
                    <button type="button" onClick={onSuivant}
                        className="btn btn-primary h-16 w-full gap-3 text-xl font-bold shadow-md">
                        {dernier ? (
                            <><Flag size={22} aria-hidden="true" /> Terminer le parcours</>
                        ) : (
                            <>Trou {trou + 1} <ChevronRight size={24} strokeWidth={2.5} aria-hidden="true" /></>
                        )}
                    </button>
                    {/* La porte de sortie, discrète : elle sert une fois sur
                        cinquante, et se mettre en travers du geste courant pour
                        le cas rare est le meilleur moyen de faire rater les
                        deux. */}
                    <button type="button" onClick={onAnnuler}
                        className="btn btn-ghost btn-sm w-full gap-2 font-semibold opacity-70">
                        <Undo2 size={15} aria-hidden="true" />
                        {releve ? 'Reprendre le trou' : "Ce n'est pas rentré"}
                    </button>
                </div>
            </div>
        </div>
    );
}
